import { getHTMLTextContent, transformChoicesToMatchChoices } from '@/lib/util';
import { Choice, MatchChoice } from '@/modules/types/choice';
import { QuizQuestion } from '@/modules/types/question';
import {
  Button,
  DeleteOutlinedIcon,
  Dialog,
  Divider,
  FormItem,
  Text,
  TextArea,
} from 'elmo-elements';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

interface MatchGroupProps {
  choices: Choice[];
  hasDelete?: boolean;
  onDelete: (item: Choice, target: Choice) => void;
}

export const MatchGroup = ({
  choices,
  onDelete,
  hasDelete,
}: MatchGroupProps): React.ReactElement => {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<QuizQuestion>();
  const [matchChoices, setMatchChoices] = useState<MatchChoice[]>(
    transformChoicesToMatchChoices(choices),
  );

  const handleChange = (key: any, value: any) => {
    setValue(key, value);
  };

  useEffect(() => {
    const transformedChoices = transformChoicesToMatchChoices(choices);

    setMatchChoices(transformedChoices);
  }, [choices]);

  return (
    <div className="choice-group">
      {matchChoices.map((matchChoice, index) => {
        const itemPosition = matchChoice.item.position * 2;
        const targetPosition = matchChoice.target.position * 2 + 1;
        return (
          <div key={matchChoice.item.id}>
            <Text>Item and Target </Text>
            {hasDelete && (
              <Button
                ariaLabel="Delete choice"
                onClick={() => {
                  if (matchChoices.length === 1) {
                    Dialog.info({
                      ariaLabel: 'Unable to delete',
                      title: `Unable to delete the last choice`,
                      content: `You cannot delete the last choice.`,
                      okLabel: 'Okay',
                    });
                    return;
                  }
                  Dialog.confirm({
                    ariaLabel: 'Delete confirm',
                    title: `Delete Choice ${index + 1}?`,
                    content: `Are you sure you want to delete choice ${
                      index + 1
                    }?`,
                    onOk() {
                      onDelete(matchChoice.item, matchChoice.target);
                    },
                    okLabel: 'Delete',
                  });
                }}
              >
                <DeleteOutlinedIcon />
              </Button>
            )}
            <Divider />
            <br />
            <div>
              <span>
                <Text>Item</Text>
                <FormItem
                  message={errors.choices?.[itemPosition]?.content?.message}
                  status={
                    errors.choices?.[itemPosition]?.content?.message
                      ? 'error'
                      : 'default'
                  }
                >
                  <TextArea
                    {...register(`choices.${itemPosition}.content`, {})}
                    onChange={(e: any) =>
                      handleChange(
                        `choices.${itemPosition}.content`,
                        e.target.value,
                      )
                    }
                    value={
                      getHTMLTextContent(
                        watch(`choices.${itemPosition}.content`),
                      ) ?? ''
                    }
                    name={`choices.${itemPosition}.content`}
                    ariaLabel={`Choice ${itemPosition}`}
                    id={`elearning-choice-${itemPosition}`}
                  />
                </FormItem>
              </span>

              <span>
                <Text>Target</Text>
                <FormItem
                  message={errors.choices?.[targetPosition]?.content?.message}
                  status={
                    errors.choices?.[targetPosition]?.content?.message
                      ? 'error'
                      : 'default'
                  }
                >
                  <TextArea
                    {...register(`choices.${targetPosition}.content`, {})}
                    onChange={(e: any) =>
                      handleChange(
                        `choices.${targetPosition}.content`,
                        e.target.value,
                      )
                    }
                    value={
                      getHTMLTextContent(
                        watch(`choices.${targetPosition}.content`),
                      ) ?? ''
                    }
                    name={`choices.${targetPosition}.content`}
                    ariaLabel={`Choice ${targetPosition}`}
                    id={`elearning-choice-${targetPosition}`}
                  />
                </FormItem>
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

MatchGroup.defaultProps = {
  hasDelete: false,
};

export default MatchGroup;
